import React, {FC, useState, useEffect} from "react"

import GenericModal from "@irati/ui/src/GenericModal"
import {Body, H2} from "@irati/ui/src/Typography"
import Flex from "@irati/ui/src/Flex"
import FormattedMessage, {injectIntl} from "@irati/utils/lang"
import dealsService from "@irati/deals/services/dealsGraph"
import {DealListSort, DealStatus, DealType} from "@irati/types"

import Loading from "@irati/ui/src/Loading"
import {adminDashboardTracking} from "@irati-tracking/admin"
import ModalWidget from "../ModalWidget"
import ShareBenefits from "./ShareBenefits"

interface Props {
  show: boolean
  onHide: () => void
}

const CompanyBenefitsModal: FC<Props> = ({show, onHide}) => {
  const [showShareBenefits, setShowShareBenefits] = useState(false)
  const [hasBenefits, setShowHasBenefits] = useState(false)
  const [load, setLoad] = useState<Promise<any> | null>(null)

  useEffect(() => {
    const updatedLoad = dealsService
      .list({statuses: [DealStatus.On], ignoreBlacklist: true, types: [DealType.Company], sort: DealListSort.Date, limit: 1})
      .then((res) => {
        if (res.results?.length) {
          setShowHasBenefits(true)
        }
      })
    setLoad(updatedLoad)
  }, [])

  const handleHide = () => {
    setShowShareBenefits(false)
    onHide()
  }

  const handleShareBenefits = () => {
    adminDashboardTracking.companyBenefitsActions("cta_shareBenefits")
    setShowShareBenefits(true)
  }

  return (
    <GenericModal hideActions show={show} customWidth={884} onHide={handleHide}>
      {showShareBenefits ? (
        <ShareBenefits onClick={() => setShowShareBenefits(false)} />
      ) : (
        <>
          <H2 marginBottom={1}>
            <FormattedMessage id="admin.components.dashboard.reporting.companyBenefits.modal.title" />
          </H2>
          <Body marginBottom={2} maxWidth="100%">
            <FormattedMessage id="admin.components.dashboard.reporting.companyBenefits.modal.text" />
          </Body>
          <Loading load={load}>
            <Flex gap={1}>
              {hasBenefits ? (
                <ModalWidget
                  title="admin.components.dashboard.reporting.companyBenefits.modal.widgets.share.title"
                  buttonText="admin.components.dashboard.reporting.companyBenefits.modal.widgets.share.buttonText"
                  emoji="💌"
                  onClick={handleShareBenefits}
                />
              ) : (
                <ModalWidget
                  title="admin.components.dashboard.reporting.companyBenefits.modal.widgets.add.title"
                  buttonText="admin.components.dashboard.reporting.companyBenefits.modal.widgets.share.buttonText"
                  emoji="➕"
                  link="/settings/customisation/benefits"
                />
              )}
              <ModalWidget
                title="admin.components.dashboard.reporting.companyBenefits.modal.widgets.explore.title"
                buttonText="admin.components.dashboard.reporting.companyBenefits.modal.widgets.explore.button"
                emoji="💼"
                href="admin.components.dashboard.reporting.companyBenefits.modal.widgets.explore.href"
                onClick={() => adminDashboardTracking.companyBenefitsActions("cta_exploreBenefits")}
                isRecommended
              />
            </Flex>
          </Loading>
        </>
      )}
    </GenericModal>
  )
}

export default injectIntl(CompanyBenefitsModal)
